'use client';

import { motion } from 'framer-motion';
import { AlertTriangle, ArrowRight, CheckCircle } from 'lucide-react';
import React from 'react';
import { Metric } from '@/components/dynamic-zone/SuccessMetrics/Metric';
import { components } from '@/types/strapi';

interface CaseStudyContentProps {
	caseStudy: components['schemas']['CaseStudy'];
}

export const CaseStudyContent = ({ caseStudy }: CaseStudyContentProps) => {
	return (
		<div className="case-study-content prose max-w-none">
			<noscript>
				<style>{`
					.case-study-content [style*='opacity'],
					.case-study-content [style*='translateX'],
					.case-study-content [style*='translateY'],
					.case-study-content [style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			{caseStudy.content && (
				<div dangerouslySetInnerHTML={{ __html: caseStudy.content }} />
			)}

			{caseStudy.summary && (
				<div className="mt-6">
					<h2 className="mb-3 text-2xl font-bold">Summary</h2>
					<p>{caseStudy.summary}</p>
				</div>
			)}

			{/* Side by side Challenges and Solutions sections */}
			<div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
				{/* Challenges section - more compact */}
				{caseStudy.challenges && caseStudy.challenges.length > 0 && (
					<div>
						<div className="mb-4 flex items-center gap-2">
							<AlertTriangle className="h-5 w-5 text-amber-500" />
							<h2 className="m-0 text-xl font-bold">
								Challenges
							</h2>
						</div>
						<div className="space-y-3">
							{caseStudy.challenges.map((challenge, index) => (
								<motion.div
									key={index}
									initial={{ opacity: 0, x: -10 }}
									whileInView={{ opacity: 1, x: 0 }}
									transition={{
										duration: 0.3,
										delay: index * 0.05,
									}}
									viewport={{ once: true }}>
									<div className="flex items-start gap-2">
										<ArrowRight className="mt-1 h-4 w-4 flex-shrink-0 text-red-500" />
										<p className="m-0 text-sm text-gray-700">
											{challenge.text}
										</p>
									</div>
								</motion.div>
							))}
						</div>
					</div>
				)}

				{/* Solutions section - more compact */}
				{caseStudy.solutions && caseStudy.solutions.length > 0 && (
					<div>
						<div className="mb-4 flex items-center gap-2">
							<CheckCircle className="h-5 w-5 text-green-500" />
							<h2 className="m-0 text-xl font-bold">Solutions</h2>
						</div>
						<div className="space-y-3">
							{caseStudy.solutions.map((solution, index) => (
								<motion.div
									key={index}
									initial={{ opacity: 0, x: 10 }}
									whileInView={{ opacity: 1, x: 0 }}
									transition={{
										duration: 0.3,
										delay: index * 0.05,
									}}
									viewport={{ once: true }}>
									<div className="flex items-start gap-2">
										<CheckCircle className="mt-1 h-4 w-4 flex-shrink-0 text-green-500" />
										<p className="m-0 text-sm text-gray-700">
											{solution.text}
										</p>
									</div>
								</motion.div>
							))}
						</div>
					</div>
				)}
			</div>

			{/* Metrics section using the Metric component */}
			{caseStudy.metrics && caseStudy.metrics.length > 0 && (
				<div className="mt-10">
					<h2 className="mb-4 text-2xl font-bold">Success Metrics</h2>
					<div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						{caseStudy.metrics.map((metric, index) => (
							<Metric
								key={index}
								value={metric.value || 0}
								title={metric.title || ''}
								description={metric.description || ''}
								iconName={metric.iconName || 'BarChart2'}
								backgroundColor={
									metric.backgroundColor || 'bg-blue-500'
								}
								gradientStartColor={
									metric.gradientStartColor || 'blue-400'
								}
								gradientEndColor={
									metric.gradientEndColor || 'blue-600'
								}
								prefix={metric.prefix || ''}
								suffix={metric.suffix || ''}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
